<template>
    <v-form @submit.prevent="addUpdate" ref="form">
        <v-card style="border-radius:4px;">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold ">{{mode}} De Minimis Benefit</span>
                <v-spacer></v-spacer>
                <help-link appId="compensation" pageId="add-deminimis" color="black"></help-link>
                <v-btn icon @click.native="closeDialog()">
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text>
                <v-layout row wrap> 
                    <v-flex xs12 sm3 class="form-row">
                        <v-select
                            label="Year"
                            :items="isEditMode?fiscal_years_all:fiscal_years"
                            :rules="[rules.required]" 
                            item-text="fiscalYear"
                            item-value="fiscalPeriodId"
                            :disabled="disabled||isEditMode"
                            v-model="fiscalPeriodId"
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm5 class="form-row">                
                         <v-select
                            label="Benefit Type"
                            :items="benefit_types"
                            :rules="[rules.required]" 
                            item-text="name"
                            item-value="benefitTypeId"
                            :disabled="disabled||mode=='Update'"
                            v-model="benefitTypeId"
                            @change="populateAnnualThreshold"
                        ></v-select>
                    </v-flex>
                    <v-flex xs12 sm4 class="form-row">                
                        <v-text-field label="Monthly Amount" 
                            v-model="monthlyAmount" 
                            :disabled="disabled"
                            :rules="[rules.required_number,rules.max_amount]"
                            type="number" min="1" step="1" value="0.00" 
                            @blur="computeAnnualPayment"
                            ></v-text-field>  
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm8 class="form-row"> 
                        <v-select
                            v-model="payOnMonth2"
                            :items="futureMonths"
                            label="Payout On"
                            :rules="[rules.required_multiple]"
                            :disabled="disabled"
                            item-text="month"
                            item-value="value"
                            multiple
                            chips
                            @change="computeAnnualPayment"
                            >
                            <!-- SELECT ALL -->
                            <template v-slot:prepend-item>
                                <v-list-tile ripple @click="toggleAllMonths">
                                <v-list-tile-action>
                                    <v-icon :color="payOnMonth2.length ? 'indigo darken-4' : ''">
                                        {{ selectAllIcon }}
                                    </v-icon>
                                </v-list-tile-action>

                                <v-list-tile-content>
                                    <v-list-tile-title>Select All</v-list-tile-title>
                                </v-list-tile-content>
                                </v-list-tile>

                                <v-divider class="mt-2"></v-divider>
                            </template>
                        </v-select>
                    </v-flex>
                    <v-flex xs12 sm4 class="form-row">  
                        <v-select
                            v-if="cutoffSettings.type!=1"
                            label="Payout Schedule"
                            :items="payoutTypes"
                            :rules="[rules.required]" 
                            item-text="description"
                            item-value="payoutMode"
                            :disabled="disabled"
                            v-model="payoutMode"           
                        ></v-select>    
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm4 class="form-row">                
                        <v-text-field label="Annual Threshold" 
                            v-model="annualThreshold" 
                            :disabled="disabled"
                            readonly
                            hint="Readonly and auto computed"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm4 class="form-row">                
                        <v-text-field label="Annual Payment" 
                            v-model="annualAmount" 
                            :disabled="disabled"
                            readonly
                            hint="Readonly and auto computed"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm4 class="form-row">                
                        <v-text-field label="Threshold Excess" 
                            v-model="thresholdExcess" 
                            :disabled="disabled"
                            readonly
                            hint="Readonly and auto computed"
                            ></v-text-field>  
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 class="form-row">            
                        <v-alert v-if="description!=null" :value="true" color="info" icon="info" outline>
                            {{description}}
                        </v-alert>  
                    </v-flex>
                </v-layout> 
                <v-layout row wrap v-if="showComment"> 
                    <v-flex xs12 sm12 class="form-comment"> 
                        <v-textarea v-model="remarks" label="Comments" rows="2" color="primary"></v-textarea>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions height="45px">
                <v-spacer></v-spacer>
                <v-btn icon @click="showComment=!showComment" title="Add Comment">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" dark>{{mode}}</v-btn>
                <v-btn v-if="mode!='Update'" @click="clearForm" :disabled="disabled" >clear</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import benefitsApi from '@/api/benefits'
import payoutTypes from '@/constants/payout-types'

export default {
    data () {
        return {
            payoutTypes,
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            rules: {
                required: value => !!value || 'Required field',
                required_number: value => !!value || 'Required field. Number only.',
                required_multiple: value => value.length>0 || 'Must select at least one month.',
                max_amount: value => value<=1000000 || 'Invalid Amount. Maximum of 1,000,000.00'  
            },
            months: [
                {month: "January", value: 1, disabled: false},
                {month: "February", value: 2, disabled: false},
                {month: "March", value: 3, disabled: false},
                {month: "April", value: 4, disabled: false},
                {month: "May", value: 5, disabled: false},
                {month: "June", value: 6, disabled: false},
                {month: "July", value: 7, disabled: false},
                {month: "August", value: 8, disabled: false},
                {month: "September", value: 9, disabled: false},
                {month: "October", value: 10, disabled: false},
                {month: "November", value: 11, disabled: false},
                {month: "December", value: 12, disabled: false}
            ],    
            mode: null,
            showComment: false,
            deminimisAllocationId: null,
            fiscalPeriodId: null,
            selectedUserId: null,
            benefitTypeId: null,
            monthlyAmount: null,
            payOnMonth: null,
            payOnMonth2: [],
            payoutMode: null,
            recurringAnnually: false,
            remarks: "",
            benefit_types: [],
            description: null,
            type: 'De Minimis',
            annualThreshold: null,
            annualAmount: null,
        }
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
    },  
    methods: {
        async setAdd(data) {
            this.mode = "Add"
            if (!this.benefit_types.length) await this.getBenefitTypes()

            this.selectedUserId = data.userId
            this.clearForm()
        },
        async setUpdate(data) {
            this.mode = "Update"
            if (!this.benefit_types.length) await this.getBenefitTypes()
            
            this.selectedUserId = data.userId
            this.deminimisAllocationId = data.deminimis.deminimisAllocationId
            this.fiscalPeriodId = data.deminimis.fiscalPeriodId
            this.benefitTypeId = data.deminimis.benefitTypeId
            this.monthlyAmount = data.deminimis.monthlyAmount
            this.payOnMonth = data.deminimis.payOnMonth
            this.payOnMonth2 = data.deminimis.payOnMonth2
            this.payoutMode = data.deminimis.payoutMode
            this.remarks = ""

            this.computeAnnualPayment()
            this.populateAnnualThreshold()
        },
        async addUpdate() {
            try {
                this.loading = true
                this.disabled = true

                if (this.$refs.form.validate()) {

                    var data = {
                        deminimisAllocationId: this.deminimisAllocationId,
                        fiscalPeriodId: this.fiscalPeriodId,
                        userId: this.selectedUserId,
                        benefitTypeId: this.benefitTypeId,                      
                        monthlyAmount: this.monthlyAmount,
                        payOnMonth: this.payOnMonth,
                        payOnMonth2: this.payOnMonth2.sort((a,b) => a-b),

                        //if 2 cutoffs per month: 1-payout on first cutoff, 2 payout on 2nd cutoff, 3 divide amount to 2 cutoffs
	                    //if 1 cutoff per month: 4-payout all in one cutoff

                        //TODO - determine isBiMonthlyCutoff in SP
                        payoutMode: this.isBiMonthlyCutoff ? this.payoutMode : 4,

                        recurringAnnually: this.recurringAnnually,
                        remarks: this.remarks,

                        //used for update history only, not saved in table
                        deminimisName: this.selectedDeminimisName,
                        fiscalYear: this.selectedFiscalYear
                    }

                    if (this.mode == 'Add') {
                        const response = await benefitsApi.addDeminimisAllocation(data)
                        if (response.status == 200) {
                            this.$emit('send', '')
                        }
                    }else{
                        const response = await benefitsApi.updateDeminimisAllocation(data)
                        if (response.status == 200) {
                            this.$emit('send', '')
                        }
                    }
                    this.closeDialog()
                }

                this.loading = false
                this.disabled = false
            }catch (error){
                this.loading = false
                this.disabled = false
                this.error = true
                this.errorMessage = error
            }
        },
        computeAnnualPayment() {
            const multiplier = this.payOnMonth2.length ? this.payOnMonth2.length : 1
            this.annualAmount = 0
            if (this.monthlyAmount > 0) {
                this.annualAmount = this.monthlyAmount * multiplier
            }
        },
        populateAnnualThreshold() {
            var data = this.benefit_types.filter(item => {
                return item.benefitTypeId == this.benefitTypeId
            })

            this.annualThreshold = data[0].annualThreshold
            this.description = data[0].description
            this.code = data[0].code
            
        },
        toggleAllMonths() {
            this.$nextTick(() => {
                if (this.allMonthsSelected) {
                    this.payOnMonth2 = []
                } else {
                    this.payOnMonth2 = this.allMonthValues.slice()
                }
                this.computeAnnualPayment()
            })
        },
        async getBenefitTypes() {
            try {
                const response = await benefitsApi.getBenefitTypes()
                if (response && response.status == 200) {
                    this.benefit_types = response.data.items.filter(item => {
                        return item.type == 'Deminimis'
                    })
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        closeDialog() {
            this.$emit('update:dialogAddEditDeminimis', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.description = null
            this.remarks = ''
            this.loading = false
            this.error = false
            this.errorMessage = ''
        },
    },
    computed: {
        thresholdExcess() {
            var total = null
            if (this.annualAmount != null && this.annualThreshold != null)
                total = parseInt(this.annualAmount) - parseInt(this.annualThreshold)

            if (total < 0) return 0
            return total
        },
        cutoffSettings() {
            return this.$store.state.cutOffSettings
        },
        isBiMonthlyCutoff() {
            if (this.cutoffSettings) {
                return (this.cutoffSettings.second_From && this.cutoffSettings.second_To)
            }
        },
        futureMonths() {
            const currentMonth = new Date().getMonth() + 1; 
            this.months.forEach(item => {
                //item.disabled = item.value < currentMonth
                item.disabled  = false //always enable all months
            })
            return this.months
        },
        fiscal_years() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods.filter(x => x.fiscalYear >= currentYear)
            }
            return [{fiscalYear: currentYear}]
        },
        fiscal_years_all() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: currentYear}]
        },
        isEditMode() {
            return this.mode == 'Update'
        },
        allMonthValues() {
            return this.futureMonths.map(m => m.value)
        },
        allMonthsSelected() {
            return this.payOnMonth2.length === this.allMonthValues.length
        },
        someMonthsSelected() {
            return this.payOnMonth2.length > 0 && !this.allMonthsSelected
        },
        selectAllIcon() {
            if (this.allMonthsSelected) return 'check_box'
            if (this.someMonthsSelected) return 'indeterminate_check_box'
            return 'check_box_outline_blank'
        },
        selectedDeminimisName() {
            if (this.benefitTypeId) 
                return this.benefit_types.find(x => x.benefitTypeId == this.benefitTypeId).name
        },
        selectedFiscalYear() {
            if (this.fiscalPeriodId)
                return this.fiscal_years_all.find(x => x.fiscalPeriodId == this.fiscalPeriodId).fiscalYear
        }
    },
    created() {}
}
</script>
<style scoped>
.form-row {
    padding: 0px 15px 0px 15px;
}
.form-comment {
    margin:5px 15px -25px 15px;
}
</style>